import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { CustomAllocationHeadCell } from '@ovComponents/4-module/widgets/portfolio/components/customAllocationHeadCell';
import type {
  PortfolioSecurity,
  PortfolioSubAccount,
} from '@ovComponents/4-module/widgets/portfolio/components/interfaces';
import {
  getPortfolioCustomTargetColumnSx,
  holdingTradeKey,
  PORTFOLIO_ALLOCATION_EDITOR_TABLE_SX,
  PORTFOLIO_ROW_HEIGHT,
} from '@ovComponents/4-module/widgets/portfolio/components/utils';
import { TESTID_PORTFOLIO_WIDGET_HOLDINGS_TABLE_ROW } from '@ovComponents/4-module/widgets/portfolio/portfolioTestIds';
import { Grid } from 'hephaestus/web';
import { Fragment, type ReactNode, useRef } from 'react';
import type { AssetClassGroupView } from './buildPortfolioAllocationView';
import { useAlignedRowHeights } from './useAlignedRowHeights';

export type PortfolioAllocationGridSlots = {
  cashRow: () => ReactNode;
  securityRow: (
    security: PortfolioSecurity,
    position: { rowIndex: number; inAssetClassGroup: boolean },
  ) => ReactNode;
  assetClassGroupRow: (
    group: AssetClassGroupView,
    context: {
      isExpanded: boolean;
      onToggleExpand: () => void;
      renderSecurityRows: () => ReactNode;
    },
  ) => ReactNode;
  securityTotalRow: () => ReactNode;
  subAccountHeadCell: (
    subAccount: PortfolioSubAccount,
    position: { subIndex: number },
  ) => ReactNode;
  subAccountSummaryCell: (
    subAccount: PortfolioSubAccount,
    position: { subIndex: number },
  ) => ReactNode;
  holdingCell: (
    security: PortfolioSecurity,
    subAccount: PortfolioSubAccount,
    position: { rowIndex: number; subIndex: number },
  ) => ReactNode;
  targetCell?: (
    security: PortfolioSecurity,
    subAccount: PortfolioSubAccount,
  ) => ReactNode;
  subAccountTotalCell: (
    subAccount: PortfolioSubAccount,
    position: { subIndex: number; rowCount: number },
  ) => ReactNode;
};

export type PortfolioAllocationGridProp = {
  securities: PortfolioSecurity[];
  subAccounts: PortfolioSubAccount[];
  assetClassGroups: AssetClassGroupView[] | null;
  isSubAccountVisible: (subAccount: PortfolioSubAccount) => boolean;
  showTargetColumn: boolean;
  collapsedAssetGroups: Set<string>;
  onToggleAssetGroup: (assetClassId: string) => void;
  leftTableHead?: ReactNode;
  slots: PortfolioAllocationGridSlots;
};

export const PortfolioAllocationGrid = ({
  securities,
  subAccounts,
  assetClassGroups,
  isSubAccountVisible,
  showTargetColumn,
  collapsedAssetGroups,
  onToggleAssetGroup,
  leftTableHead,
  slots,
}: PortfolioAllocationGridProp) => {
  const visibleSubAccounts = subAccounts.filter(isSubAccountVisible);
  const frozenColumnsRef = useRef<HTMLDivElement>(null);
  const accountColumnsRef = useRef<HTMLDivElement>(null);
  useAlignedRowHeights(frozenColumnsRef, accountColumnsRef);

  const renderHoldingRow = (security: PortfolioSecurity, rowIndex: number) => (
    <TableRow
      key={security.id}
      data-testid={TESTID_PORTFOLIO_WIDGET_HOLDINGS_TABLE_ROW(rowIndex)}
    >
      {security.subAccounts
        .filter(isSubAccountVisible)
        .map((subAccount, subIndex) => (
          <Fragment key={holdingTradeKey(security.id, subAccount.id)}>
            {slots.holdingCell(security, subAccount, { rowIndex, subIndex })}
            {showTargetColumn && slots.targetCell?.(security, subAccount)}
          </Fragment>
        ))}
    </TableRow>
  );

  return (
    <Grid container>
      <Grid ref={frozenColumnsRef} size='auto'>
        <Table>
          <TableHead>{leftTableHead}</TableHead>

          <TableBody>
            {slots.cashRow()}

            {assetClassGroups
              ? assetClassGroups.map((group) => (
                  <Fragment key={group.assetClassId}>
                    {slots.assetClassGroupRow(group, {
                      isExpanded: !collapsedAssetGroups.has(group.assetClassId),
                      onToggleExpand: () =>
                        onToggleAssetGroup(group.assetClassId),
                      renderSecurityRows: () =>
                        group.items.map((security, idx) => (
                          <Fragment key={security.id}>
                            {slots.securityRow(security, {
                              rowIndex: group.startIndex + idx,
                              inAssetClassGroup: true,
                            })}
                          </Fragment>
                        )),
                    })}
                  </Fragment>
                ))
              : securities.map((security, index) => (
                  <Fragment key={security.id}>
                    {slots.securityRow(security, {
                      rowIndex: index,
                      inAssetClassGroup: false,
                    })}
                  </Fragment>
                ))}

            {slots.securityTotalRow()}
          </TableBody>
        </Table>
      </Grid>

      <Grid ref={accountColumnsRef} sx={{ overflowX: 'auto' }} size='grow'>
        <Table
          sx={
            showTargetColumn ? PORTFOLIO_ALLOCATION_EDITOR_TABLE_SX : undefined
          }
        >
          <TableHead>
            <TableRow>
              {visibleSubAccounts.map((subAccount, subIndex) => (
                <Fragment key={subAccount.id}>
                  {slots.subAccountHeadCell(subAccount, { subIndex })}
                  {showTargetColumn && (
                    <CustomAllocationHeadCell index={subIndex} />
                  )}
                </Fragment>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow>
              {visibleSubAccounts.map((subAccount, subIndex) => (
                <Fragment key={subAccount.id}>
                  {slots.subAccountSummaryCell(subAccount, { subIndex })}
                  {showTargetColumn && (
                    <TableCell sx={getPortfolioCustomTargetColumnSx()} />
                  )}
                </Fragment>
              ))}
            </TableRow>

            {assetClassGroups
              ? assetClassGroups.map((group) => (
                  <Fragment key={group.assetClassId}>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell
                        colSpan={
                          visibleSubAccounts.length * (showTargetColumn ? 2 : 1)
                        }
                        sx={{ height: PORTFOLIO_ROW_HEIGHT.standard }}
                      />
                    </TableRow>

                    {!collapsedAssetGroups.has(group.assetClassId) &&
                      group.items.map((security, idx) =>
                        renderHoldingRow(security, group.startIndex + idx),
                      )}
                  </Fragment>
                ))
              : securities.map((security, index) =>
                  renderHoldingRow(security, index),
                )}

            <TableRow>
              {visibleSubAccounts.map((subAccount, subIndex) => (
                <Fragment key={subAccount.id}>
                  {slots.subAccountTotalCell(subAccount, {
                    subIndex,
                    rowCount: securities.length,
                  })}
                  {showTargetColumn && (
                    <TableCell sx={getPortfolioCustomTargetColumnSx()} />
                  )}
                </Fragment>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  );
};
